import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Clock,
  EuroIcon,
  Calendar,
  Download,
  FileText,
  Target,
  Activity,
  Award,
  BarChart3,
  Users,
  Briefcase,
  AlertTriangle,
  TrendingUp,
  CreditCard,
  CalendarCheck,
  Timer,
  Palmtree,
  Zap,
  CheckCircle2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAdvancedPermissions } from '../hooks/useAdvancedPermissions';
import { useTheme } from '../contexts/ThemeContext';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#6366f1'];

// --- Premium Components (Internal) ---

const getColorClasses = (color) => {
  switch (color) {
    case 'blue':    return { bg: 'bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/40 dark:to-slate-900', icon: 'bg-blue-500/20 text-blue-500 dark:text-blue-400', bar: 'bg-blue-500' };
    case 'emerald': return { bg: 'bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/40 dark:to-slate-900', icon: 'bg-emerald-500/20 text-emerald-500 dark:text-emerald-400', bar: 'bg-emerald-500' };
    case 'purple':  return { bg: 'bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/40 dark:to-slate-900', icon: 'bg-purple-500/20 text-purple-500 dark:text-purple-400', bar: 'bg-purple-500' };
    default:        return { bg: 'bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/40 dark:to-slate-900', icon: 'bg-amber-500/20 text-amber-500 dark:text-amber-400', bar: 'bg-amber-500' };
  }
};

const getThresholdBadge = (value, thresholds) => {
  if (!thresholds) return null;
  if (value >= thresholds.good)   return { label: 'Excellent', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400' };
  if (value >= thresholds.medium) return { label: 'Moyen', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' };
  return { label: 'Faible', cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-400' };
};

const StatCard = ({ title, value, subLabel, icon: Icon, color, delay, progress, thresholds }) => {
  const cls = getColorClasses(color);
  const badge = getThresholdBadge(typeof progress === 'number' ? progress : null, thresholds);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className={`relative overflow-hidden rounded-2xl p-6 border border-gray-200 dark:border-slate-700/50 backdrop-blur-xl ${cls.bg}`}
    >
      <div className="relative z-10 flex justify-between items-start">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-gray-500 dark:text-slate-400 text-sm font-medium uppercase tracking-wider">{title}</p>
            {badge && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>}
          </div>
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{value}</h3>
          {subLabel && <p className="text-gray-400 dark:text-slate-500 text-xs mt-1">{subLabel}</p>}
          {typeof progress === 'number' && (
            <div className="mt-3">
              <div className="w-full h-1.5 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-700 ${cls.bar}`} style={{ width: `${Math.min(progress, 100)}%` }} />
              </div>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ml-3 flex-shrink-0 ${cls.icon}`}>
          <Icon size={24} />
        </div>
      </div>
    </motion.div>
  );
};

const SectionCard = ({ title, icon: Icon, children, delay, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.98 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5, delay }}
    className={`bg-white dark:bg-slate-800/40 backdrop-blur-md border border-gray-200 dark:border-slate-700/50 rounded-2xl p-6 shadow-xl flex flex-col ${className}`}
  >
    <div className="flex items-center gap-2 mb-6 border-b border-gray-200 dark:border-slate-700/50 pb-4">
      {Icon && <Icon className="text-blue-500 dark:text-blue-400" size={20} />}
      <h3 className="text-lg font-bold text-gray-800 dark:text-slate-200">{title}</h3>
    </div>
    <div className="flex-1 w-full relative">
      {children}
    </div>
  </motion.div>
);

// --- Employee Selector ---

const AVATAR_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#6366f1'];
const getAvatarColor = (firstName = '', lastName = '') => {
  const code = (firstName.charCodeAt(0) || 0) + (lastName.charCodeAt(0) || 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};
const getInitials = (emp) => `${emp.firstName?.[0] || ''}${emp.lastName?.[0] || ''}`.toUpperCase();

const EmployeeAvatar = ({ employee, size = 8 }) => (
  <div className={`w-${size} h-${size} rounded-xl overflow-hidden flex-shrink-0`}>
    {employee.photoURL ? (
      <img src={employee.photoURL} alt="" className="w-full h-full object-cover" />
    ) : (
      <div
        className="w-full h-full flex items-center justify-center text-white text-sm font-bold"
        style={{ backgroundColor: employee.color || getAvatarColor(employee.firstName, employee.lastName) }}
      >
        {getInitials(employee)}
      </div>
    )}
  </div>
);

const EmployeeSelector = ({ employees, selectedEmployee, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const activeEmployees = employees.filter(e => e.isActive !== false);

  return (
    <div className="relative w-full md:w-80">
      <button
        onClick={() => setIsOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-xl px-4 py-3 text-left shadow-lg hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
      >
        {selectedEmployee ? (
          <div className="flex items-center gap-3 min-w-0">
            <EmployeeAvatar employee={selectedEmployee} size={8} />
            <span className="text-gray-800 dark:text-slate-200 font-medium truncate">
              {selectedEmployee.firstName} {selectedEmployee.lastName}
            </span>
          </div>
        ) : (
          <span className="text-gray-400 dark:text-slate-500">Sélectionnez un collaborateur</span>
        )}
        <ChevronDown size={16} className={`text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full mt-2 w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden max-h-64 overflow-y-auto"
          >
            {activeEmployees.map(emp => (
              <button
                key={emp.id}
                onClick={() => { onSelect(emp.id); setIsOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors text-left ${selectedEmployee?.id === emp.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
              >
                <EmployeeAvatar employee={emp} size={8} />
                <span className="text-gray-800 dark:text-slate-200 font-medium truncate">
                  {emp.firstName} {emp.lastName}
                </span>
                {selectedEmployee?.id === emp.id && (
                  <CheckCircle2 size={16} className="text-blue-500 ml-auto flex-shrink-0" />
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- Employee Banner ---

const EmployeeBanner = ({ employee }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-200/60 dark:border-blue-700/30"
  >
    <div className="w-14 h-14 rounded-2xl overflow-hidden flex-shrink-0">
      {employee.photoURL ? (
        <img src={employee.photoURL} alt="" className="w-full h-full object-cover" />
      ) : (
        <div
          className="w-full h-full flex items-center justify-center text-white text-xl font-bold"
          style={{ backgroundColor: getAvatarColor(employee.firstName, employee.lastName) }}
        >
          {getInitials(employee)}
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white truncate">
        {employee.firstName} {employee.lastName}
      </h2>
      <div className="flex flex-wrap items-center gap-2 mt-1">
        {employee.role && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400">
            <Briefcase size={12} /> {employee.role}
          </span>
        )}
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${
          employee.isAutoEntrepreneur
            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
        }`}>
          {employee.isAutoEntrepreneur ? 'Auto-entrepreneur' : 'Salarié'}
        </span>
        {employee.hourlyRate && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400 bg-white dark:bg-slate-800/60 px-2 py-0.5 rounded-full border border-gray-200 dark:border-slate-700">
            <EuroIcon size={11} /> {employee.hourlyRate}€/h
          </span>
        )}
      </div>
    </div>
  </motion.div>
);

// --- Global Dashboard (vue admin sans sélection) ---

const GlobalDashboard = ({ employees, perEmployeeHours, totalInterventions, plannedHours, completedHours, onSelect, formatHours }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const activeEmployees = employees.filter(e => e.isActive !== false && !e.isArchived);
  const activeCount = Object.keys(perEmployeeHours).length;

  // Build bar chart data: only employees with hours, sorted descending
  const chartData = activeEmployees
    .filter(emp => perEmployeeHours[emp.id] > 0)
    .map(emp => ({
      name: emp.firstName,
      fullName: `${emp.firstName} ${emp.lastName}`,
      hours: Number((perEmployeeHours[emp.id] || 0).toFixed(1)),
      empId: emp.id,
      color: emp.color || getAvatarColor(emp.firstName, emp.lastName),
    }))
    .sort((a, b) => b.hours - a.hours);

  const completionRate = plannedHours > 0 ? Math.round((completedHours / plannedHours) * 100) : 0;

  const gridStroke = isDark ? '#334155' : '#e5e7eb';
  const axisStroke = isDark ? '#94a3b8' : '#9ca3af';
  const tooltipStyle = isDark
    ? { backgroundColor: '#1e293b', borderColor: '#334155', borderRadius: '10px', color: '#f1f5f9', fontSize: 13 }
    : { backgroundColor: '#ffffff', borderColor: '#e5e7eb', borderRadius: '10px', color: '#111827', fontSize: 13 };
  const cursorFill = isDark ? '#334155' : '#e5e7eb';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Global KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Heures planifiées" value={formatHours(plannedHours)} subLabel="Tous les intervenants" icon={Calendar} color="blue" delay={0.05} />
        <StatCard title="Heures réalisées" value={formatHours(completedHours)} subLabel={`${completionRate}% de réalisation`} icon={Clock} color="emerald" delay={0.1} progress={completionRate} />
        <StatCard title="Interventions" value={totalInterventions} subLabel="Ce mois-ci" icon={CalendarCheck} color="amber" delay={0.15} />
        <StatCard title="Collaborateurs actifs" value={activeCount} subLabel="Avec au moins 1 RDV" icon={Users} color="purple" delay={0.2} />
      </div>

      {/* Bar chart by employee */}
      {chartData.length > 0 && (
        <SectionCard title="Heures par collaborateur" icon={BarChart3} delay={0.25}>
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} strokeOpacity={0.6} vertical={false} />
                <XAxis
                  dataKey="name"
                  stroke={axisStroke}
                  tickLine={false}
                  axisLine={false}
                  dy={8}
                  tick={{ fontSize: 12 }}
                />
                <YAxis stroke={axisStroke} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  cursor={{ fill: cursorFill, opacity: 0.3 }}
                  formatter={(value, name, props) => [`${value}h`, props.payload.fullName]}
                />
                <Bar dataKey="hours" name="Heures" radius={[6, 6, 0, 0]} barSize={40}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      )}

      {/* Employee cards — grid on mobile, wraps naturally */}
      <div>
        <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">
          Voir le détail d'un collaborateur
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-3">
          {activeEmployees.map((emp) => (
            <button
              key={emp.id}
              onClick={() => onSelect(emp.id)}
              className="flex flex-col items-center gap-2 p-3 bg-white dark:bg-slate-800/40 border border-gray-200 dark:border-slate-700/50 rounded-2xl hover:border-blue-400 dark:hover:border-blue-500/60 hover:shadow-lg hover:-translate-y-0.5 transition-all group"
            >
              <EmployeeAvatar employee={emp} size={10} />
              <span className="text-xs font-semibold text-gray-800 dark:text-slate-200 text-center group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate w-full">
                {emp.firstName}
              </span>
              {perEmployeeHours[emp.id] > 0 && (
                <span className="text-xs text-gray-400 dark:text-slate-500 font-medium">
                  {formatHours(perEmployeeHours[emp.id])}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// --- Main Component ---

const EmployeeReports = ({ db, currentUser }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [clientStats, setClientStats] = useState({});
  const [clients, setClients] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plannedHours, setPlannedHours] = useState(0);
  const [completedHours, setCompletedHours] = useState(0);
  const [clientContracts, setClientContracts] = useState({});
  const [workedDays, setWorkedDays] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [productivityStats, setProductivityStats] = useState({
    fillRate: 0,
    avgHoursPerDay: 0,
    completionRate: 0,
    totalInterventions: 0,
    weekendHours: 0,
    avgDuration: 0,
    topDay: '-'
  });
  const [weeklyTrends, setWeeklyTrends] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [exportLoading, setExportLoading] = useState(null);
  const [exportStatus, setExportStatus] = useState(null);
  const [perEmployeeHours, setPerEmployeeHours] = useState({});
  const [globalTotalInterventions, setGlobalTotalInterventions] = useState(0);

  const { theme } = useTheme();
  const { hasPermission: hasAdvancedPermission, loading: permissionsLoading } = useAdvancedPermissions({ userId: currentUser?.id });

  const canViewReports = hasAdvancedPermission({ resource: 'reports', action: 'read' });
  const canExportReports = hasAdvancedPermission({ resource: 'reports', action: 'write' });
  const canViewAllReports = hasAdvancedPermission({ resource: 'reports', action: 'manage' });
  const canViewOwnReport = hasAdvancedPermission({ resource: 'appointments', action: 'read' });
  const hasReportsAccess = canViewReports || canViewAllReports || canViewOwnReport;

  useEffect(() => {
    const selectedEmployeeFromStorage = localStorage.getItem('selectedEmployeeForReports');
    if (selectedEmployeeFromStorage) {
      try {
        const employeeData = JSON.parse(selectedEmployeeFromStorage);
        const employee = employees.find(emp => emp.id === employeeData.id);
        if (employee) {
          setSelectedEmployee(employee);
          localStorage.removeItem('selectedEmployeeForReports');
        }
      } catch (error) {
        console.error('Error parsing employee data:', error);
        localStorage.removeItem('selectedEmployeeForReports');
      }
    } else if (canViewOwnReport && !canViewAllReports && currentUser?.userId) {
      const ownEmployee = employees.find(emp => emp.id === currentUser.userId || emp.userId === currentUser.userId);
      if (ownEmployee) {
        handleEmployeeChange(ownEmployee.id);
      }
    }
  }, [canViewOwnReport, canViewAllReports, currentUser, employees]);

  const getPageTitle = () => {
    if (canViewAllReports) return "Rapports Collaborateurs";
    if (canViewOwnReport) return "Mes Statistiques";
    return "Rapports";
  };

  const fetchEmployees = useCallback(async () => {
    try {
      const employeesSnapshot = await getDocs(collection(db, 'employees'));
      const employeesList = employeesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(emp => emp.role !== 'beneficiaire');
      setEmployees(employeesList);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setError("Erreur lors du chargement des employés");
      setIsLoading(false);
    }
  }, [db]);

  const fetchClients = useCallback(async () => {
    try {
      const clientsSnapshot = await getDocs(collection(db, "clients"));
      const clientsMap = {};
      clientsSnapshot.docs.forEach(doc => {
        clientsMap[doc.id] = { id: doc.id, ...doc.data() };
      });
      setClients(clientsMap);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setError("Erreur lors du chargement des clients");
    }
  }, [db]);

  const calculateDuration = (startTime, endTime) => {
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diff = (end - start) / (1000 * 60);
    const hours = Math.floor(diff / 60);
    const minutes = Math.round(diff % 60);
    return { hours, minutes };
  };

  const fetchClientContracts = useCallback(async () => {
    try {
      const contractsSnapshot = await getDocs(collection(db, "contracts"));
      const contractsMap = {};
      contractsSnapshot.docs.forEach(doc => {
        contractsMap[doc.id] = doc.data();
      });
      setClientContracts(contractsMap);
    } catch (error) {
      console.error("Error fetching client contracts:", error);
      setError("Erreur lors du chargement des contrats des clients");
    }
  }, [db]);

  const processAppointments = useCallback((appointments, startDate, endOfMonth, employee) => {
    let totalPlannedHours = 0;
    let totalCompletedHours = 0;
    let totalAmount = 0;
    let totalForecastAmount = 0;
    const clientStatsData = {};
    const now = new Date();
    const workedDaysSet = new Set();
    const weeklyHours = Array(7).fill(0);
    let weekendHours = 0;
    let showAlertFlag = false;

    appointments.forEach(appointment => {
      const duration = calculateDuration(appointment.startTime, appointment.endTime);
      const durationInHours = duration.hours + duration.minutes / 60;
      totalPlannedHours += durationInHours;

      const clientContract = clientContracts[appointment.clientId];
      let hourlyRate;

      if (employee.isAutoEntrepreneur && employee.hourlyRate) {
        hourlyRate = parseFloat(employee.hourlyRate);
      } else if (employee.isAutoEntrepreneur && !employee.hourlyRate) {
        showAlertFlag = true;
        hourlyRate = clientContract ? parseFloat(clientContract.hourlyRate) : 0;
      } else {
        hourlyRate = clientContract ? parseFloat(clientContract.hourlyRate) : 0;
      }

      totalForecastAmount += durationInHours * hourlyRate;

      if (appointment.date <= now) {
        totalCompletedHours += durationInHours;
        totalAmount += durationInHours * hourlyRate;
      }

      const dayOfWeek = appointment.date.getDay();
      weeklyHours[dayOfWeek] += durationInHours;
      workedDaysSet.add(appointment.date.toDateString());

      // Weekend Calculation (0 = Sunday, 6 = Saturday)
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        weekendHours += durationInHours;
      }

      if (!clientStatsData[appointment.clientId]) {
        clientStatsData[appointment.clientId] = { hours: 0, amount: 0, hourlyRate: hourlyRate };
      }
      clientStatsData[appointment.clientId].hours += durationInHours;
      clientStatsData[appointment.clientId].amount += durationInHours * hourlyRate;
    });

    const fillRate = workedDaysSet.size > 0 ? (totalPlannedHours / (workedDaysSet.size * 8)) * 100 : 0;
    const avgHoursPerDay = workedDaysSet.size > 0 ? totalPlannedHours / workedDaysSet.size : 0;
    const completionRate = totalPlannedHours > 0 ? (totalCompletedHours / totalPlannedHours) * 100 : 0;

    // Derived Statistics
    const totalInterventions = appointments.length;
    const avgDuration = totalInterventions > 0 ? totalPlannedHours / totalInterventions : 0;

    // Find Top Day
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const maxDayIndex = weeklyHours.indexOf(Math.max(...weeklyHours));
    const topDay = Math.max(...weeklyHours) > 0 ? days[maxDayIndex] : '-';

    const monthStats = {
      month: startDate.toLocaleString('default', { month: 'long', year: 'numeric' }),
      totalPlannedHours: totalPlannedHours,
      totalCompletedHours: totalCompletedHours,
      totalAmount: totalAmount,
      totalForecastAmount: totalForecastAmount
    };

    const weeklyTrendData = weeklyHours.map((hours, index) => ({
      day: days[index].substring(0, 3), // Lun, Mar, etc.
      hours: Number(hours.toFixed(2))
    }));

    const topClientsData = Object.entries(clientStatsData)
      .sort(([, a], [, b]) => b.hours - a.hours)
      .slice(0, 3)
      .map(([clientId, stats]) => ({
        client: clients[clientId] ? `${clients[clientId].firstName} ${clients[clientId].lastName}` : `Client ${clientId}`,
        hours: stats.hours,
        amount: stats.amount
      }));

    setMonthlyStats(monthStats);
    setClientStats(clientStatsData);
    setPlannedHours(totalPlannedHours);
    setCompletedHours(totalCompletedHours);
    setWorkedDays(workedDaysSet.size);
    setShowAlert(showAlertFlag);

    // Updated Productivity Stats including new metrics
    setProductivityStats({
      fillRate: Number(fillRate.toFixed(1)),
      avgHoursPerDay: Number(avgHoursPerDay.toFixed(1)),
      completionRate: Number(completionRate.toFixed(1)),
      totalInterventions,
      weekendHours: Number(weekendHours.toFixed(1)),
      avgDuration: Number(avgDuration.toFixed(1)),
      topDay
    });

    setWeeklyTrends(weeklyTrendData);
    setTopClients(topClientsData);
  }, [clientContracts, clients]);

  useEffect(() => {
    fetchEmployees();
    fetchClients();
    fetchClientContracts();
  }, [fetchEmployees, fetchClients, fetchClientContracts]);

  const calculateTotalHours = useCallback((appointments, startDate) => {
    let totalHours = 0;
    let totalCompletedHours = 0;
    const now = new Date();
    const byEmployee = {};

    appointments.forEach(appointment => {
      const duration = calculateDuration(appointment.startTime, appointment.endTime);
      const durationInHours = duration.hours + duration.minutes / 60;
      totalHours += durationInHours;
      if (appointment.date <= now) {
        totalCompletedHours += durationInHours;
      }
      if (appointment.employeeId) {
        byEmployee[appointment.employeeId] = (byEmployee[appointment.employeeId] || 0) + durationInHours;
      }
    });

    const monthStats = {
      month: startDate.toLocaleString('default', { month: 'long', year: 'numeric' }),
      totalHours: totalHours,
      totalCompletedHours: totalCompletedHours
    };

    setMonthlyStats(monthStats);
    setClientStats({});
    setPlannedHours(totalHours);
    setCompletedHours(totalCompletedHours);
    setWorkedDays(0);
    setPerEmployeeHours(byEmployee);
    setGlobalTotalInterventions(appointments.length);
  }, []);

  const fetchMonthData = useCallback(async (employee, date) => {
    setIsLoading(true);
    setError(null);
    try {
      const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
      startOfMonth.setHours(0, 0, 0, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      const startTimestamp = Timestamp.fromDate(startOfMonth);
      const endTimestamp = Timestamp.fromDate(endOfMonth);

      let appointmentsQuery;
      if (employee && employee.id) {
        appointmentsQuery = query(
          collection(db, 'appointments'),
          where('employeeId', '==', employee.id),
          where('date', '>=', startTimestamp),
          where('date', '<=', endTimestamp)
        );
      } else {
        appointmentsQuery = query(
          collection(db, 'appointments'),
          where('date', '>=', startTimestamp),
          where('date', '<=', endTimestamp)
        );
      }

      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      const appointments = appointmentsSnapshot.docs.map(doc => {
        const data = doc.data();
        return { ...data, id: doc.id, date: data.date.toDate() };
      });

      if (employee && employee.id) {
        processAppointments(appointments, startOfMonth, endOfMonth, employee);
      } else {
        calculateTotalHours(appointments, startOfMonth);
      }
    } catch (error) {
      console.error("Error fetching month data:", error);
      setError("Erreur lors du chargement des données mensuelles: " + error.message);
    } finally {
      setIsLoading(false);
    }
  }, [db, processAppointments, calculateTotalHours]);

  useEffect(() => {
    if (selectedEmployee) {
      fetchMonthData(selectedEmployee, currentDate);
    } else if (canViewAllReports) {
      fetchMonthData(null, currentDate);
    }
  }, [selectedEmployee, currentDate, fetchMonthData, canViewAllReports]);

  const formatHours = (hours) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours}h${minutes.toString().padStart(2, '0')}`;
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleEmployeeChange = (employeeId) => {
    if (canViewAllReports || (canViewOwnReport && (employeeId === currentUser.userId || employees.find(e => e.id === employeeId)?.userId === currentUser.userId))) {
      const employee = employees.find(emp => emp.id === employeeId);
      setSelectedEmployee(employee);
      setMonthlyStats(null);
      setClientStats({});
      fetchMonthData(employee, currentDate);
    } else {
      setError("Vous n'avez pas la permission de voir les rapports des autres employés.");
    }
  };

  const exportToPDF = async () => {
    if (!selectedEmployee || !monthlyStats) return;
    setExportLoading('pdf');
    setExportStatus(null);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let yPosition = margin;
      doc.setFontSize(20); doc.setFont('helvetica', 'bold');
      doc.text('Rapport Mensuel', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      doc.setFontSize(14); doc.setFont('helvetica', 'normal');
      doc.text(`Mois: ${currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;
      doc.text(`Employé: ${selectedEmployee.firstName} ${selectedEmployee.lastName}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;
      doc.setFontSize(12); doc.setFont('helvetica', 'bold');
      doc.text('Résumé du Mois', margin, yPosition);
      yPosition += 8;

      const summaryData = [
        ['Jours travaillés', workedDays],
        ['Nombre d\'interventions', productivityStats.totalInterventions],
        ['Heures planifiées', formatHours(plannedHours)],
        ['Heures effectuées', formatHours(completedHours)],
        ['Heures week-end', formatHours(productivityStats.weekendHours)],
        ['Taux horaire', selectedEmployee.hourlyRate ? `${selectedEmployee.hourlyRate}€` : 'Variable'],
        ['Montant prévisionnel', `${monthlyStats.totalForecastAmount.toFixed(2)}€`],
        ['Montant réalisé', `${monthlyStats.totalAmount.toFixed(2)}€`],
        ['Taux de remplissage', `${productivityStats.fillRate}%`],
        ['Moyenne quotidienne', `${productivityStats.avgHoursPerDay}h`],
        ['Taux de réalisation', `${productivityStats.completionRate}%`]
      ];
      autoTable(doc, {
        startY: yPosition,
        head: [['Statistique', 'Valeur']],
        body: summaryData,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 10, cellPadding: 3 },
        margin: { left: margin, right: margin }
      });
      // ... (Rest of PDF generation)
      yPosition = doc.lastAutoTable.finalY + 10;
      if (Object.keys(clientStats).length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('Détails par Client', margin, yPosition);
        yPosition += 8;
        const clientData = Object.entries(clientStats).map(([clientId, stats]) => {
          const client = clients[clientId];
          const percentage = (stats.hours / plannedHours) * 100;
          return [
            client ? `${client.lastName} ${client.firstName}` : `Client ${clientId}`,
            formatHours(stats.hours),
            typeof stats.hourlyRate === 'number' ? `${stats.hourlyRate.toFixed(2)}€` : 'N/A',
            `${stats.amount.toFixed(2)}€`,
            `${percentage.toFixed(1)}%`
          ];
        });
        autoTable(doc, {
          startY: yPosition,
          head: [['Client', 'Heures', 'Taux horaire', 'Montant', 'Pourcentage']],
          body: clientData,
          theme: 'grid',
          headStyles: { fillColor: [52, 152, 219], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 8, cellPadding: 2 },
          margin: { left: margin, right: margin }
        });
      }
      doc.save(`rapport_${selectedEmployee.firstName}_${selectedEmployee.lastName}.pdf`);

      setExportStatus('success');
      setTimeout(() => setExportStatus(null), 2000);
    } catch (error) {
      console.error(error);
      setExportStatus('error');
    } finally {
      setExportLoading(null);
    }
  };


  if (isLoading || permissionsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-4"></div>
          <p className="text-gray-500 dark:text-slate-400">Chargement des rapports...</p>
        </div>
      </div>
    );
  }

  if (!hasReportsAccess) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-6">
        <div className="bg-white dark:bg-slate-800/50 p-8 rounded-2xl border border-rose-500/30 text-center max-w-md">
          <AlertTriangle size={48} className="mx-auto text-rose-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Accès refusé</h2>
          <p className="text-gray-500 dark:text-slate-400">Vous n'avez pas les permissions nécessaires.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-slate-100 p-6 font-sans">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-500">
              {getPageTitle()}
            </h1>
            <p className="text-gray-500 dark:text-slate-400 mt-1">Analyse détaillée de la performance</p>
          </div>

          {canViewAllReports && (
            <EmployeeSelector
              employees={employees}
              selectedEmployee={selectedEmployee}
              onSelect={handleEmployeeChange}
            />
          )}
        </div>

        {/* Alert for Missing Rate */}
        <AnimatePresence>
          {showAlert && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-rose-900/20 border border-rose-500/50 rounded-xl p-4 flex items-start gap-3"
            >
              <AlertTriangle className="text-rose-400 shrink-0" size={20} />
              <p className="text-rose-200 text-sm">
                <strong>Attention :</strong> L'employé est auto-entrepreneur sans taux horaire défini.
                Le calcul utilise les taux horaires des clients.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {monthlyStats && (
          <>
            {/* Month Navigation */}
            <div className="flex items-center justify-between bg-white dark:bg-slate-800/40 backdrop-blur-sm p-4 rounded-xl border border-gray-200 dark:border-slate-700/50">
              <button onClick={() => changeMonth(-1)} className="p-3 hover:bg-gray-100 dark:hover:bg-slate-700/50 rounded-lg text-gray-400 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                <ChevronLeft size={24} />
              </button>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-slate-200 capitalize">
                {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </h2>
              <button onClick={() => changeMonth(1)} className="p-3 hover:bg-gray-100 dark:hover:bg-slate-700/50 rounded-lg text-gray-400 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                <ChevronRight size={24} />
              </button>
            </div>

            {/* Employee Banner */}
            {selectedEmployee && <EmployeeBanner employee={selectedEmployee} />}

            {/* Global Dashboard — admin sans sélection */}
            {!selectedEmployee && canViewAllReports && (
              <GlobalDashboard
                employees={employees}
                perEmployeeHours={perEmployeeHours}
                totalInterventions={globalTotalInterventions}
                plannedHours={plannedHours}
                completedHours={completedHours}
                onSelect={handleEmployeeChange}
                formatHours={formatHours}
              />
            )}

            {/* Top Stats Overview — vue individuelle uniquement */}
            {selectedEmployee && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              <StatCard
                title="Heures Planifiées"
                value={formatHours(plannedHours)}
                subLabel="Ce mois-ci"
                icon={Calendar}
                color="blue"
                delay={0.1}
              />
              <StatCard
                title="Heures Réalisées"
                value={formatHours(completedHours)}
                subLabel={`Sur ${formatHours(plannedHours)} planifiées`}
                icon={Clock}
                color="emerald"
                delay={0.2}
              />
              <StatCard
                title="Revenus"
                value={`${monthlyStats.totalAmount.toFixed(0)}€`}
                subLabel={`Prév. : ${(monthlyStats.totalForecastAmount ?? 0).toFixed(0)}€`}
                icon={EuroIcon}
                color="amber"
                delay={0.3}
                progress={
                  (monthlyStats.totalForecastAmount ?? 0) > 0
                    ? (monthlyStats.totalAmount / monthlyStats.totalForecastAmount) * 100
                    : 0
                }
              />
              <StatCard
                title="Taux de remplissage"
                value={`${productivityStats.fillRate}%`}
                subLabel={`${productivityStats.avgHoursPerDay}h / jour moy.`}
                icon={Target}
                color="purple"
                delay={0.35}
                progress={productivityStats.fillRate}
                thresholds={{ good: 80, medium: 50 }}
              />
              <StatCard
                title="Taux de réalisation"
                value={`${productivityStats.completionRate}%`}
                subLabel={`${workedDays} jour${workedDays > 1 ? 's' : ''} travaillé${workedDays > 1 ? 's' : ''}`}
                icon={TrendingUp}
                color="blue"
                delay={0.4}
                progress={productivityStats.completionRate}
                thresholds={{ good: 90, medium: 70 }}
              />
            </div>
            )}

            {selectedEmployee && (
              <div className="space-y-8">

                {/* Full Width Weekly Chart */}
                <SectionCard title="Évolution Hebdomadaire" icon={BarChart3} delay={0.5}>
                  <div className="h-[350px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={weeklyTrends} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                        <XAxis dataKey="day" stroke="#94a3b8" tickLine={false} axisLine={false} dy={10} />
                        <YAxis stroke="#94a3b8" tickLine={false} axisLine={false} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', borderRadius: '8px', color: '#f1f5f9' }}
                          cursor={{ fill: '#334155', opacity: 0.4 }}
                        />
                        <Bar dataKey="hours" name="Heures" radius={[6, 6, 0, 0]} barSize={50}>
                          {weeklyTrends.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </SectionCard>

                {/* 2-Col Split: Pie Chart + Detailed Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Pie Chart */}
                  <SectionCard title="Répartition par Client" icon={Users} delay={0.6}>
                    <div className="flex flex-col md:flex-row items-center w-full h-[300px]">
                      <div className="w-full md:w-1/2 h-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={Object.entries(clientStats).map(([id, s]) => ({
                                name: clients[id]?.lastName || `Client ${id}`,
                                value: s.hours
                              }))}
                              cx="50%" cy="50%" innerRadius={70} outerRadius={100}
                              paddingAngle={5} dataKey="value"
                            >
                              {Object.keys(clientStats).map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', borderRadius: '8px' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="w-full md:w-1/2 p-4 space-y-3">
                        {topClients.map((client, i) => (
                          <div key={i} className="flex items-center justify-between text-sm p-2 rounded-lg bg-gray-50 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700/30">
                            <div className="flex items-center gap-3">
                              <div className="w-4 h-4 rounded-full shadow-lg" style={{ backgroundColor: COLORS[i % COLORS.length] }}></div>
                              <span className="text-gray-700 dark:text-slate-300 font-medium truncate max-w-[120px]">{client.client}</span>
                            </div>
                            <span className="font-bold text-gray-900 dark:text-white bg-gray-200 dark:bg-slate-700/50 px-2 py-0.5 rounded">{formatHours(client.hours)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </SectionCard>

                  {/* Detailed Analysis KPIs */}
                  <SectionCard title="Analyse Détaillée" icon={Zap} delay={0.7} className="justify-center">
                    <div className="grid grid-cols-2 gap-4 h-full">

                      {/* Intervention Count */}
                      <div className="p-4 bg-gray-50 dark:bg-slate-900/40 rounded-xl border border-gray-200 dark:border-slate-700/50 flex flex-col items-center justify-center text-center">
                        <div className="p-2 rounded-lg bg-blue-500/20 text-blue-500 dark:text-blue-400 mb-2">
                          <CalendarCheck size={20} />
                        </div>
                        <div className="text-gray-500 dark:text-slate-400 text-xs uppercase font-bold mb-1">Total RDV</div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">{productivityStats.totalInterventions}</div>
                      </div>

                      {/* Weekend Hours */}
                      <div className="p-4 bg-gray-50 dark:bg-slate-900/40 rounded-xl border border-gray-200 dark:border-slate-700/50 flex flex-col items-center justify-center text-center">
                        <div className="p-2 rounded-lg bg-indigo-500/20 text-indigo-500 dark:text-indigo-400 mb-2">
                          <Palmtree size={20} />
                        </div>
                        <div className="text-gray-500 dark:text-slate-400 text-xs uppercase font-bold mb-1">Week-end</div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatHours(productivityStats.weekendHours)}</div>
                        <div className="text-xs text-gray-400 dark:text-slate-500">Heures le sam/dim</div>
                      </div>

                      {/* Avg Duration */}
                      <div className="p-4 bg-gray-50 dark:bg-slate-900/40 rounded-xl border border-gray-200 dark:border-slate-700/50 flex flex-col items-center justify-center text-center">
                        <div className="p-2 rounded-lg bg-amber-500/20 text-amber-500 dark:text-amber-400 mb-2">
                          <Timer size={20} />
                        </div>
                        <div className="text-gray-500 dark:text-slate-400 text-xs uppercase font-bold mb-1">Durée Moyenne</div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">{productivityStats.avgDuration}h</div>
                        <div className="text-xs text-gray-400 dark:text-slate-500">Par intervention</div>
                      </div>

                      {/* Top Day */}
                      <div className="p-4 bg-gray-50 dark:bg-slate-900/40 rounded-xl border border-gray-200 dark:border-slate-700/50 flex flex-col items-center justify-center text-center">
                        <div className="p-2 rounded-lg bg-emerald-500/20 text-emerald-500 dark:text-emerald-400 mb-2">
                          <TrendingUp size={20} />
                        </div>
                        <div className="text-gray-500 dark:text-slate-400 text-xs uppercase font-bold mb-1">Pic d'activité</div>
                        <div className="text-xl font-bold text-gray-900 dark:text-white truncate max-w-full px-2">{productivityStats.topDay}</div>
                      </div>

                    </div>
                  </SectionCard>
                </div>

                {/* Full Width Table */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
                  className="bg-white dark:bg-slate-800/40 backdrop-blur-md border border-gray-200 dark:border-slate-700/50 rounded-2xl overflow-hidden shadow-xl"
                >
                  <div className="p-6 border-b border-gray-200 dark:border-slate-700/50 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <FileText className="text-blue-500 dark:text-blue-400" size={24} />
                        Détail des Activités
                      </h3>
                      <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">Répartition précise par client et revenus</p>
                    </div>

                    <button
                      onClick={exportToPDF}
                      disabled={exportLoading}
                      className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-500/20 transition-all flex items-center gap-2"
                    >
                      {exportLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Download size={18} />}
                      Exporter le Rapport PDF
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-600 dark:text-slate-400">
                      <thead className="bg-gray-50 dark:bg-slate-900/80 text-gray-700 dark:text-slate-200">
                        <tr>
                          <th className="px-6 py-4 font-bold uppercase tracking-wider text-xs">Client</th>
                          <th className="px-6 py-4 font-bold uppercase tracking-wider text-xs">Heures</th>
                          <th className="px-6 py-4 font-bold uppercase tracking-wider text-xs">Taux/h</th>
                          <th className="px-6 py-4 font-bold uppercase tracking-wider text-xs">Montant</th>
                          <th className="px-6 py-4 font-bold uppercase tracking-wider text-xs w-1/3">Impact %</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-slate-700/50">
                        {Object.entries(clientStats).map(([clientId, stats], rowIndex) => {
                          const percent = (stats.hours / plannedHours) * 100;
                          return (
                            <tr key={clientId} className={`hover:bg-blue-50/50 dark:hover:bg-slate-700/30 transition-colors ${rowIndex % 2 === 1 ? 'bg-gray-50/60 dark:bg-slate-800/20' : ''}`}>
                              <td className="px-6 py-5 text-gray-900 dark:text-white font-semibold">
                                {clients[clientId] ? `${clients[clientId].lastName} ${clients[clientId].firstName}` : 'Inconnu'}
                              </td>
                              <td className="px-6 py-5">
                                <span className="bg-gray-100 dark:bg-slate-800 px-3 py-1 rounded-full text-gray-800 dark:text-slate-200">{formatHours(stats.hours)}</span>
                              </td>
                              <td className="px-6 py-5 text-gray-500 dark:text-slate-400 text-sm">
                                {typeof stats.hourlyRate === 'number' && stats.hourlyRate > 0 ? `${stats.hourlyRate.toFixed(2)}€` : <span className="text-gray-300 dark:text-slate-600">—</span>}
                              </td>
                              <td className="px-6 py-5 text-emerald-600 dark:text-emerald-400 font-bold text-base">{stats.amount.toFixed(2)}€</td>
                              <td className="px-6 py-5">
                                <div className="flex items-center gap-3">
                                  <div className="w-full h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500" style={{ width: `${percent}%` }}></div>
                                  </div>
                                  <span className="font-bold text-gray-900 dark:text-white min-w-[3rem] text-right">{percent.toFixed(1)}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeReports;
